import { on as onEvent } from "common/utils/ui/event";
import { track } from "jiffy-analytics";
import initDetailsToggle from "components/jiffy/reviews-list-item/reviews-list-item";
import initRecaptchaModal from "components/jiffy/recaptcha-modal/recaptcha-modal";
// eslint-disable-next-line import/no-cycle
import initProductReviewsControls from "components/jiffy/product-reviews-controls/product-reviews-controls";
import initReviewsListDynamic from "components/jiffy/reviews-list-dynamic/reviews-list-dynamic";
import "common/customer/redirect_to_help";
import "./reviews-list.scss";
import { api } from "jiffy-api";

const $getContainer = () => document.querySelector(".js-reviews-list-container");
const $getAnchor = () => document.querySelector(".js-reviews-anchor");
const $getPagination = () => document.querySelector(".jiffy-pagination");
const $getReviewSection = () => document.querySelector(".js-reviews-anchor");

const initTrackReviewScroll = () => {
  if (!$getReviewSection()) return;

  const $toObserve = $getPagination() || $getReviewSection();
  const intersectionObserver = new window.IntersectionObserver(
    ([entry], observer) => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);
      }
    },
    {
      root: null,
      threshold: 0.1
    }
  );

  intersectionObserver.observe($toObserve);
};

// Add Made context to API parameters if available in dataset
const addMadeContextToParams = (apiParams, dataset) => {
  if (dataset.fromMade === "true") {
    apiParams.made_blank = dataset.madeBlank;
    apiParams.made_color = dataset.madeColor;
    apiParams.from_made = "true";
  }
};

export const loadReviews = async () => {
  const $container = $getContainer();
  if (!$container) return;

  const $spinner = document.querySelector(".js-reviews-list-spinner");
  $spinner.classList.toggle("hidden", false);

  const { productId, digitalAssetId, displayAll, isBare } = $container.dataset;

  const apiParams = {
    ...window.reviews,
    digital_asset_id: digitalAssetId,
    bare: isBare,
    display_all: displayAll === "true"
  };

  // Add Made context if available in dataset
  addMadeContextToParams(apiParams, $container.dataset);

  const { reviews } = await api.product.fetchProductReviews({ productId }, apiParams);

  $container.innerHTML = reviews;
  $spinner.classList.toggle("hidden", true);

  if (window.reviews?.page) {
    const originUri = new URL(window.location);
    const { searchParams } = originUri;
    searchParams.set("page", window.reviews.page);
    searchParams.delete("customizations[]");
    searchParams.delete("kind");
    searchParams.delete("sort");
    searchParams.delete("search");

    window.history.replaceState(null, null, `${originUri.pathname}?${searchParams.toString()}`);
  }

  initDetailsToggle();
  initTrackReviewScroll();
};

const initPagination = () => {
  const callback = event => {
    event.preventDefault();
    const $anchor = $getAnchor();
    const url = new URL(event.target.href || "", window.origin);
    const page = url.searchParams.get("page") || 1;
    window.reviews.page = page;
    loadReviews();

    $anchor.scrollIntoView({ behavior: "smooth" });
    track("product_detailed_reviews_page_nav");
  };

  onEvent("click", ".js-reviews-list-container .jiffy-pagination a", callback, true);
};

const loadDefaultParams = () => {
  if (window.reviews !== undefined) return;

  const url = new URL(window.location);
  window.reviews = {};
  ["page", "sort", "kind", "search"].forEach(reviewParam => {
    if (url.searchParams.get(reviewParam)) {
      window.reviews[reviewParam] = url.searchParams.get(reviewParam);
    }
  });

  // Handle customizations array parameter
  if (url.searchParams.getAll("customizations[]").length > 0) {
    window.reviews.customizations = url.searchParams.getAll("customizations[]");
  }

  // Note: Form controls should already be set by server-side rendering using params[:sort], params[:search], etc.
  // We only need to ensure JavaScript state matches the form state for dynamic interactions
};

// Check if we're on the dedicated reviews page vs PDP
const isOnDedicatedReviewsPage = () => window.location.pathname.includes("/reviews");

// Track if this is the initial load (not triggered by user interaction)
let isInitialLoad = true;

// Enhanced loadReviews that respects PDP vs reviews page context
export const loadReviewsWithContext = async () => {
  // On PDP: only load reviews on initial page load, not on control changes
  // On Reviews Page: always load reviews (initial + control changes)
  if (!isOnDedicatedReviewsPage() && !isInitialLoad) {
    // On PDP after initial load, don't make API calls - controls should redirect
    return;
  }

  await loadReviews();
  isInitialLoad = false;
};

export default async () => {
  loadDefaultParams();

  // Always load initial reviews (both PDP and reviews page)
  loadReviewsWithContext();

  initPagination();
  initRecaptchaModal();
  initProductReviewsControls();
  initReviewsListDynamic();
};
